import { createContext, useContext, type ReactNode } from "react";
import { Stack, styled, Typography, type StackProps } from "@mui/material";
import { ChipSelectOption } from "./ChipSelectOption";

type ChipSelectValue = string | string[] | null;

interface ChipSelectContextValue {
  value: ChipSelectValue;
  multiple: boolean;
  name?: string;
  disabled: boolean;
  allowEmpty: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>, optionValue: string) => void;
}

const ChipSelectContext = createContext<ChipSelectContextValue | null>(null);

export const useChipSelectContext = () => {
  const context = useContext(ChipSelectContext);
  if (!context) {
    throw new Error("ChipSelect.Option must be used within a ChipSelect");
  }
  return context;
};

export interface ChipSelectProps extends Omit<StackProps, "onChange"> {
  value?: ChipSelectValue;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: ChipSelectValue) => void;
  multiple?: boolean;
  label?: ReactNode;
  name?: string;
  disabled?: boolean;
  allowEmpty?: boolean;
  children?: ReactNode;
}

const StyledContainer = styled(Stack)({
  alignItems: "center",
});

const StyledLabel = styled(Typography)(({ theme }) => ({
  marginRight: theme.spacing(1),
})) as typeof Typography;

export const ChipSelect = ({
  value,
  onChange,
  multiple = false,
  label,
  name,
  disabled = false,
  allowEmpty = false,
  children,
  ...stackProps
}: ChipSelectProps) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, optionValue: string) => {
    if (!onChange) {
      return;
    }

    if (multiple) {
      const currentValues = (value as string[]) || [];
      const isSelected = currentValues.includes(optionValue);

      if (isSelected) {
        const newValues = currentValues.filter((v) => v !== optionValue);
        onChange(event, newValues);
      } else {
        onChange(event, [...currentValues, optionValue]);
      }
    } else {
      const isSelected = value === optionValue;

      if (isSelected && allowEmpty) {
        onChange(event, null);
      } else if (!isSelected) {
        onChange(event, optionValue);
      }
    }
  };

  const contextValue: ChipSelectContextValue = {
    value: value ?? null,
    multiple,
    name,
    disabled,
    allowEmpty,
    onChange: handleChange,
  };

  return (
    <ChipSelectContext.Provider value={contextValue}>
      <StyledContainer direction="row" gap={1} {...stackProps}>
        {label && (
          <StyledLabel variant="labelLarge" color="textSecondary">
            {label}
          </StyledLabel>
        )}
        {children}
      </StyledContainer>
    </ChipSelectContext.Provider>
  );
};

ChipSelect.Option = ChipSelectOption;
